import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { LanguageCode } from '@language';
import {
    ApplicationConfig,
    ApplicationConfigService,
    BaseContainer,
    LocalStorageID,
    MaintenanceContent,
    MaintenanceNotification,
    ShHtmlParserComponent,
    ShIconModule
} from '@stylehaven/components';

@Component({
    standalone: true,
    selector: 'med-maintenance-notification',
    templateUrl: './maintenance-notification.component.html',
    styleUrls: ['./maintenance-notification.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ShIconModule, ShHtmlParserComponent]
})
export class MaintenanceNotificationContainerComponent extends BaseContainer implements OnInit {
    private readonly LOCAL_STORAGE_ID: string = LocalStorageID.MaintenanceNotification;

    private closedNotification$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public showNotification$: Observable<boolean>;
    public notificationContent$: Observable<MaintenanceContent | null>;

    constructor(private readonly configService: ApplicationConfigService) {
        super();
    }

    ngOnInit(): void {
        this.initMaintenanceSettings();
    }

    private initMaintenanceSettings(): void {
        const maintenance$: Observable<MaintenanceNotification> = this.configService
            .getConfig()
            .pipe(map((config: ApplicationConfig) => config.maintenanceNotification));

        this.showNotification$ = combineLatest([maintenance$, this.closedNotification$]).pipe(
            map(([maintenance, closed]) => maintenance.enabled && !this.hasClosedNotification() && !closed)
        );

        this.notificationContent$ = maintenance$.pipe(
            map(maintenanceNotification => this.getMaintenanceNotificationContent(maintenanceNotification))
        );
    }

    private getMaintenanceNotificationContent(
        maintenanceNotification: MaintenanceNotification
    ): MaintenanceContent | null {
        const currentLang: LanguageCode = this.translateService.getCurrentLanguage();
        const maintenanceNotificationContent: MaintenanceContent | null = maintenanceNotification.content
            ? maintenanceNotification.content[currentLang]
            : null;

        if (!maintenanceNotificationContent) {
            console.error(
                'MaintenanceNotificationContainerComponent:: No maintenance notification content translation found'
            );
        }

        return maintenanceNotificationContent;
    }

    private hasClosedNotification(): boolean {
        const closedNotification = localStorage.getItem(this.LOCAL_STORAGE_ID);

        if (!closedNotification) {
            return false;
        }

        return !!closedNotification;
    }

    public onClose(): void {
        localStorage.setItem(this.LOCAL_STORAGE_ID, 'true');
        this.closedNotification$.next(true);
    }
}
